import { scaleBand, scaleLinear } from 'd3-scale';
import { stack, stackOffsetDiverging, stackOrderNone } from 'd3-shape';
import { cx } from '../cx';
import { Tooltip } from '../Tooltip/Tooltip';
import { type ChartFocus, ChartFrame, type ChartTable } from './ChartFrame';
import {
  AXIS_GAP_PX,
  AXIS_LINE_PX,
  type ChartPoint,
  type ChartSeries,
  categoriesOf,
  estimateTextWidth,
  fillClass,
  formatValue,
  labelBudget,
  labelStep,
  MAX_GUTTER_SHARE,
  NO_DATA,
  PLOT_PAD_PX,
  seriesColor,
  truncateLabel,
  valueAt,
  valueDomain,
  valueTicks,
} from './chartModel';

/**
 * BARS: GROUPED OR STACKED, VERTICAL OR HORIZONTAL (`reports.md` T1).
 *
 * A bar's length is read against a baseline, so the value scale ALWAYS contains zero -
 * `valueDomain` enforces it - and a negative value grows the other way from that baseline
 * rather than being clipped or flipped. Stacking with negatives is the one case a naive
 * cumulative sum gets silently wrong, which is why `stackOffsetDiverging` does it: positives
 * stack up from zero, negatives stack down from it, and the two never cross.
 *
 * HORIZONTAL IS NOT A ROTATION. It is a different layout with a different gutter (the
 * category labels move to the left and get real room), and it is the layout for long
 * category names - which is exactly what the "30 characters" state in the gallery shows.
 */
export interface BarChartProps {
  readonly name: string;
  readonly titleHidden?: boolean | undefined;
  readonly series: readonly ChartSeries[];
  readonly layout?: 'vertical' | 'horizontal' | undefined;
  readonly stacked?: boolean | undefined;
  readonly height?: number | undefined;
  readonly loading?: boolean | undefined;
  readonly refetching?: boolean | undefined;
  readonly emptyMessage?: string | undefined;
  /** The heading over the category column in the hidden table: "Month", "Package". */
  readonly categoryHeading?: string | undefined;
  readonly format?: ((value: number) => string) | undefined;
  /**
   * A click on a bar, with the category it belongs to. Absent means the chart is a
   * picture: no pointer cursor, no hit target, nothing that looks pressable and is not.
   */
  readonly onSelect?: ((point: ChartPoint) => void) | undefined;
}

export function BarChart({
  name,
  titleHidden,
  series,
  layout = 'vertical',
  stacked = false,
  height = 240,
  loading = false,
  refetching = false,
  emptyMessage,
  categoryHeading = 'Category',
  format = formatValue,
  onSelect,
}: BarChartProps) {
  const categories = categoriesOf(series);
  const empty = categories.length === 0 || series.length === 0;

  const table: ChartTable = {
    categoryHeading,
    columns: series.map((entry) => entry.label),
    rows: categories.map((category) => ({
      label: category.label,
      values: series.map((entry) => valueAt(entry, category.key)),
    })),
  };

  // A legend of one is furniture: the chart's own name already says what the one series is.
  const legend =
    series.length > 1
      ? series.map((entry, index) => ({
          id: entry.id,
          label: entry.label,
          color: seriesColor(entry.tone, index, series.length),
        }))
      : [];

  const announce = ({ series: seriesIndex, point }: ChartFocus): string => {
    const entry = series[seriesIndex];
    const category = categories[point];
    if (entry === undefined || category === undefined) return '';
    const value = valueAt(entry, category.key);
    return `${entry.label}, ${category.label}: ${value === null ? NO_DATA : format(value)}`;
  };

  return (
    <ChartFrame
      name={name}
      {...(titleHidden === undefined ? {} : { titleHidden })}
      height={height}
      loading={loading}
      refetching={refetching}
      {...(emptyMessage === undefined ? {} : { emptyMessage })}
      empty={empty}
      legend={legend}
      table={table}
      rows={series.length}
      columns={categories.length}
      announce={announce}
    >
      {({ width, height: plotHeight, focus }) => (
        <BarPlot
          width={width}
          height={plotHeight}
          series={series}
          categories={categories}
          layout={layout}
          stacked={stacked}
          focus={focus}
          format={format}
          animate={!refetching}
          {...(onSelect === undefined ? {} : { onSelect })}
        />
      )}
    </ChartFrame>
  );
}

interface BarPlotProps {
  readonly width: number;
  readonly height: number;
  readonly series: readonly ChartSeries[];
  readonly categories: ReturnType<typeof categoriesOf>;
  readonly layout: 'vertical' | 'horizontal';
  readonly stacked: boolean;
  readonly focus: ChartFocus | null;
  readonly format: (value: number) => string;
  readonly animate: boolean;
  readonly onSelect?: ((point: ChartPoint) => void) | undefined;
}

/**
 * One bar per series per category, laid out. The stacking maths runs even when `stacked`
 * is false, because a grouped chart is the same rectangles at a different x - and one
 * function that answers "where does this bar start and end" is one place to get it wrong.
 */
function BarPlot({
  width,
  height,
  series,
  categories,
  layout,
  stacked,
  focus,
  format,
  animate,
  onSelect,
}: BarPlotProps) {
  /**
   * A NULL CONTRIBUTES NOTHING TO A STACK, which is the only coherent reading: a stack's
   * height is the sum of what is there, and a bucket we could not read adds nothing to a
   * sum. The bar itself is still not drawn - the renderer skips it - so a missing series
   * leaves a gap in the stack rather than a segment of zero height with a tooltip.
   */
  const rows: Record<string, number>[] = categories.map((category) => {
    const row: Record<string, number> = {};
    for (const entry of series) row[entry.id] = valueAt(entry, category.key) ?? 0;
    return row;
  });

  /**
   * `stackOffsetDiverging` and not the default: it is the one offset that handles a
   * negative value honestly, stacking positives up from zero and negatives down from it
   * instead of adding a negative into a running total and folding the stack through itself.
   */
  const stacks = stack<Record<string, number>>()
    .keys(series.map((entry) => entry.id))
    .order(stackOrderNone)
    .offset(stackOffsetDiverging)(rows);

  const values: number[] = [];
  for (const [seriesIndex, entry] of series.entries()) {
    for (const [categoryIndex, category] of categories.entries()) {
      const value = valueAt(entry, category.key);
      if (value === null) continue;
      if (stacked) {
        const stackRange = stacks[seriesIndex]?.[categoryIndex];
        if (stackRange !== undefined) values.push(stackRange[0], stackRange[1]);
      } else {
        values.push(value);
      }
    }
  }
  const domain = valueDomain(values);

  /**
   * THE GUTTERS ARE CAPPED, AND THE LABELS ARE THEN CUT TO FIT THEM. A currency format can
   * make a tick label wider than the plot it labels; a gutter that simply grew would leave
   * the chart with no room to draw in, and one that did not grow would paint text outside
   * the SVG, where it is clipped in silence. So the cap wins and the text is cut to the cap.
   */
  const ticks = valueTicks(domain, 4);
  const valueGutter = Math.min(
    Math.max(...ticks.map((tick) => estimateTextWidth(format(tick)))) + AXIS_GAP_PX,
    width * MAX_GUTTER_SHARE,
  );
  const categoryGutter = layout === 'horizontal' ? Math.min(Math.max(width * 0.3, 56), 160) : 0;

  const left = layout === 'horizontal' ? categoryGutter + AXIS_GAP_PX : valueGutter;
  const right = PLOT_PAD_PX;
  const top = PLOT_PAD_PX;
  const bottom = AXIS_LINE_PX + AXIS_GAP_PX;

  const plotWidth = Math.max(1, width - left - right);
  const plotHeight = Math.max(1, height - top - bottom);

  const band = scaleBand<string>()
    .domain(categories.map((category) => category.key))
    .range(layout === 'horizontal' ? [top, top + plotHeight] : [left, left + plotWidth])
    .padding(0.2);

  const value = scaleLinear()
    .domain([...domain])
    .range(layout === 'horizontal' ? [left, left + plotWidth] : [top + plotHeight, top]);

  const groupBand = scaleBand<string>()
    .domain(series.map((entry) => entry.id))
    .range([0, band.bandwidth()])
    .padding(0.08);

  const zero = value(0);
  const step = labelStep(
    categories.length,
    layout === 'horizontal' ? plotHeight : plotWidth,
    layout === 'horizontal' ? AXIS_LINE_PX : 3 * estimateTextWidth('MMM'),
  );

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${String(width)} ${String(height)}`}
      aria-hidden="true"
      /**
       * `w-full` ON TOP OF THE WIDTH ATTRIBUTE, and it is the same bug one level out that
       * `ChartFrame`'s own `w-full` fixes. An `<svg width="640">` has an INTRINSIC width of
       * 640, so every ancestor sized by its content - a card, a grid cell, a flex item -
       * takes 640 from it, the observer then measures 640 back, and the chart settles at
       * its fallback inside a 343px phone. A CSS width of 100% contributes nothing to
       * max-content, so the container decides and the attribute only sets the coordinate
       * system, which the viewBox keeps in step.
       */
      className="w-full"
    >
      <AxisTicks
        ticks={ticks}
        scale={value}
        layout={layout}
        left={left}
        top={top}
        width={width}
        plotWidth={plotWidth}
        plotHeight={plotHeight}
        gutter={valueGutter}
        format={format}
      />
      {series.map((entry, seriesIndex) => {
        const color = seriesColor(entry.tone, seriesIndex, series.length);
        return (
          <g key={entry.id}>
            {categories.map((category, categoryIndex) => {
              const raw = valueAt(entry, category.key);
              if (raw === null) return null;
              const bandStart = band(category.key);
              if (bandStart === undefined) return null;

              const stackRange = stacks[seriesIndex]?.[categoryIndex];
              const lower = stacked ? (stackRange?.[0] ?? 0) : Math.min(0, raw);
              const upper = stacked ? (stackRange?.[1] ?? 0) : Math.max(0, raw);
              const negative = raw < 0;

              const thickness = stacked ? band.bandwidth() : groupBand.bandwidth();
              const offset = stacked ? 0 : (groupBand(entry.id) ?? 0);

              const near = value(lower);
              const far = value(upper);
              const focused =
                focus !== null && focus.series === seriesIndex && focus.point === categoryIndex;

              const rect =
                layout === 'horizontal'
                  ? {
                      x: Math.min(near, far),
                      y: bandStart + offset,
                      width: Math.max(1, Math.abs(far - near)),
                      height: Math.max(1, thickness),
                    }
                  : {
                      x: bandStart + offset,
                      y: Math.min(near, far),
                      width: Math.max(1, thickness),
                      height: Math.max(1, Math.abs(far - near)),
                    };

              return (
                <Tooltip
                  key={category.key}
                  label={`${entry.label}, ${category.label}: ${format(raw)}`}
                >
                  <rect
                    x={rect.x}
                    y={rect.y}
                    width={rect.width}
                    height={rect.height}
                    rx={2}
                    {...(onSelect === undefined
                      ? {}
                      : { onClick: () => onSelect(category), className: undefined })}
                    className={cx(
                      fillClass(color),
                      animate && drawInClass(layout, negative),
                      focused && 'stroke-2 stroke-accent',
                    )}
                  />
                </Tooltip>
              );
            })}
          </g>
        );
      })}
      {/*
          THE BASELINE, DRAWN ONLY WHEN IT IS NOT THE EDGE. With no negative value the zero
          line sits exactly on the frame's own bottom (or left) and a second hairline there
          is furniture; with one, it is the line every bar is measured from and it has to be
          visible in both layouts.
        */}
      {domain[0] < 0 &&
        (layout === 'vertical' ? (
          <line
            x1={left}
            x2={left + plotWidth}
            y1={zero}
            y2={zero}
            className="stroke-line-strong"
            strokeWidth={1}
          />
        ) : (
          <line
            x1={zero}
            x2={zero}
            y1={top}
            y2={top + plotHeight}
            className="stroke-line-strong"
            strokeWidth={1}
          />
        ))}
      <CategoryLabels
        categories={categories}
        band={band}
        layout={layout}
        step={step}
        left={left}
        top={top}
        plotWidth={plotWidth}
        plotHeight={plotHeight}
        categoryGutter={layout === 'horizontal' ? categoryGutter : band.step() * step - AXIS_GAP_PX}
      />
    </svg>
  );
}

/** A bar grows from its own baseline, which is the zero line and not the frame's edge. */
function drawInClass(layout: 'vertical' | 'horizontal', negative: boolean): string {
  if (layout === 'horizontal') {
    return negative ? 'chart-grow-from-right' : 'chart-grow-from-left';
  }
  return negative ? 'chart-grow-from-top' : 'chart-grow-from-bottom';
}

function AxisTicks({
  ticks,
  scale,
  layout,
  left,
  top,
  width,
  plotWidth,
  plotHeight,
  gutter,
  format,
}: {
  readonly ticks: readonly number[];
  readonly scale: (value: number) => number;
  readonly layout: 'vertical' | 'horizontal';
  readonly left: number;
  readonly top: number;
  readonly width: number;
  readonly plotWidth: number;
  readonly plotHeight: number;
  readonly gutter: number;
  readonly format: (value: number) => string;
}) {
  return (
    <g>
      {ticks.map((tick) => {
        const position = scale(tick);
        /**
         * A horizontal chart's value labels sit UNDER the plot, centred on their own line,
         * so the outermost two are bounded by the edges of the drawing rather than by the
         * gutter - which is the bound a chart forgets and then paints past.
         */
        const label =
          layout === 'horizontal'
            ? truncateLabel(format(tick), labelBudget(position, 0, width, gutter * 2))
            : truncateLabel(format(tick), gutter - AXIS_GAP_PX);
        return layout === 'horizontal' ? (
          <g key={tick}>
            <line
              x1={position}
              x2={position}
              y1={top}
              y2={top + plotHeight}
              className="stroke-line-subtle"
              strokeWidth={1}
            />
            <text
              x={position}
              y={top + plotHeight + AXIS_LINE_PX - 4}
              textAnchor="middle"
              className="fill-ink-muted text-caption tabular-nums"
            >
              {label}
            </text>
          </g>
        ) : (
          <g key={tick}>
            <line
              x1={left}
              x2={left + plotWidth}
              y1={position}
              y2={position}
              className="stroke-line-subtle"
              strokeWidth={1}
            />
            <text
              x={left - AXIS_GAP_PX}
              y={position + 4}
              textAnchor="end"
              className="fill-ink-muted text-caption tabular-nums"
            >
              {label}
            </text>
          </g>
        );
      })}
    </g>
  );
}

function CategoryLabels({
  categories,
  band,
  layout,
  step,
  left,
  top,
  plotWidth,
  plotHeight,
  categoryGutter,
}: {
  readonly categories: ReturnType<typeof categoriesOf>;
  readonly band: ReturnType<typeof scaleBand<string>>;
  readonly layout: 'vertical' | 'horizontal';
  readonly step: number;
  readonly left: number;
  readonly top: number;
  readonly plotWidth: number;
  readonly plotHeight: number;
  readonly categoryGutter: number;
}) {
  return (
    <g>
      {categories.map((category, index) => {
        if (index % step !== 0) return null;
        const start = band(category.key);
        if (start === undefined) return null;
        const centre = start + band.bandwidth() / 2;
        const label = truncateLabel(
          category.label,
          layout === 'horizontal'
            ? categoryGutter
            : labelBudget(centre, left, left + plotWidth, categoryGutter),
        );
        if (label === '') return null;
        return layout === 'horizontal' ? (
          <text
            key={category.key}
            x={left - AXIS_GAP_PX}
            y={centre + 4}
            textAnchor="end"
            className="fill-ink-muted text-caption"
          >
            {label}
          </text>
        ) : (
          <text
            key={category.key}
            x={centre}
            y={top + plotHeight + AXIS_LINE_PX}
            textAnchor="middle"
            className="fill-ink-muted text-caption"
          >
            {label}
          </text>
        );
      })}
    </g>
  );
}
